import React from 'react';
import { motion } from 'framer-motion';
import { Bell, Leaf, Heart, Sparkles } from 'lucide-react';
import { Card } from '../ui';
import NewsletterForm from '../forms/NewsletterForm';

const Newsletter = () => {
  const benefits = [
    {
      icon: Bell,
      title: 'Weekly Wellness Tips',
      description: 'Receive mindfulness practices and meditation guidance'
    },
    {
      icon: Leaf,
      title: 'Exclusive Content',
      description: 'Access to special workshops and wellness resources'
    },
    {
      icon: Heart,
      title: 'Community Updates',
      description: 'Stay connected with our wellness community events'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-soft-white to-pragyavan-cream/30 relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-pragyavan-gold/5 via-transparent to-transparent" />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Card
            className="relative overflow-hidden border-0 shadow-2xl bg-white/80 backdrop-blur-md"
            rounded="large"
            padding="none"
          >
            {/* Card Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-pragyavan-gold/10 to-transparent rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-sage-green/10 to-transparent rounded-full blur-3xl pointer-events-none" />

            <div className="p-8 md:p-16 text-center relative z-10">
              {/* Header */}
              <div className="mb-12">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-pragyavan-gold/20 to-sage-green/20 flex items-center justify-center shadow-inner">
                  <Bell className="w-10 h-10 text-pragyavan-brown" />
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pragyavan-brown/5 text-pragyavan-brown text-xs font-medium mb-4">
                  <Sparkles className="w-3 h-3" />
                  <span>Join Our Community</span>
                </div>
                <h2 className="text-3xl md:text-5xl font-display font-bold text-pragyavan-brown mb-6">
                  Stay Connected with <span className="text-gradient-gold">Pragyavan</span>
                </h2>
                <p className="text-lg text-warm-gray max-w-2xl mx-auto leading-relaxed">
                  Join our wellness community and receive mindful insights,
                  meditation tips, and exclusive updates delivered to your inbox.
                </p>
              </div>

              {/* Benefits */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
              >
                {benefits.map((benefit, index) => {
                  const IconComponent = benefit.icon;
                  const colors = [
                    { bg: 'from-pragyavan-gold/10 to-pragyavan-gold/5', icon: 'text-pragyavan-gold', glow: 'group-hover:shadow-pragyavan-gold/20' },
                    { bg: 'from-sage-green/10 to-sage-green/5', icon: 'text-sage-green', glow: 'group-hover:shadow-sage-green/20' },
                    { bg: 'from-pragyavan-brown/10 to-pragyavan-brown/5', icon: 'text-pragyavan-brown', glow: 'group-hover:shadow-pragyavan-brown/20' }
                  ];
                  const color = colors[index % colors.length];

                  return (
                    <motion.div
                      key={index}
                      whileHover={{ y: -8, scale: 1.02 }}
                      transition={{ type: "spring", stiffness: 400, damping: 25 }}
                      className={`group p-6 rounded-2xl bg-white/70 backdrop-blur-sm border border-white/60 hover:bg-white hover:shadow-xl ${color.glow} transition-all duration-300 relative overflow-hidden`}
                    >
                      {/* Decorative corner gradient */}
                      <div className={`absolute -top-10 -right-10 w-24 h-24 bg-gradient-to-br ${color.bg} rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                      <div className={`w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${color.bg} flex items-center justify-center ${color.icon} shadow-sm group-hover:shadow-md transition-all duration-300 relative z-10`}>
                        <IconComponent className="w-7 h-7" />
                      </div>
                      <h3 className="font-display font-semibold text-lg text-pragyavan-brown mb-2 group-hover:text-pragyavan-gold transition-colors duration-300 relative z-10">
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-warm-gray leading-relaxed relative z-10">
                        {benefit.description}
                      </p>
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Newsletter Form */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="max-w-md mx-auto bg-white p-2 rounded-2xl shadow-lg shadow-pragyavan-gold/10 border border-pragyavan-gold/10"
              >
                <NewsletterForm />
              </motion.div>

              {/* Privacy Note */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="mt-8"
              >
                <p className="text-xs text-warm-gray/60">
                  By subscribing, you agree to our Privacy Policy. We never share your information.
                </p>
              </motion.div>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default Newsletter;